/**
 * Reference Positions — a Snowflake-backed position snapshot that sits beside
 * the upload-driven derivative recon flow.
 *
 * Ported from recon-dashboard/frontend/src/components/reference/ReferencePositions.jsx.
 * That repo reads SANDBOX.LAUNCHER.POSITIONS; this app reads the live
 * Clearwater lot view via the recon server's /positions endpoints.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PamGrid } from '@1823-partners/core';
import {
  Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart,
  ResponsiveContainer, Tooltip as RTooltip, XAxis, YAxis,
} from 'recharts';

import {
  fetchPositions,
  fetchPositionsByCurrency,
  fetchPositionsByIssuer,
  fetchPositionsDates,
  fetchPositionsSummary,
} from '../../api';
import {
  fmt, fmtN, formatCell, isDateCol, isNumCol, toDateObj,
} from '../../utils/derivativeReconFormat';

// A month-end feed lands a couple of weeks after close, so flag anything
// materially older than one reporting period.
const STALE_AFTER_DAYS = 45;

const PALETTE = [
  '#5b7fff', '#38bdf8', '#34d399', '#fbbf24',
  '#f87171', '#a78bfa', '#f472b6', '#22d3ee',
];

const REF_COLS = [
  { field: 'acct', headerName: 'Account', width: 160 },
  { field: 'id', headerName: 'Identifier', width: 130 },
  { field: 'des', headerName: 'Description', flex: 1, minWidth: 220 },
  { field: 'tdt', headerName: 'Trade Dt', width: 110 },
  { field: 'sdt', headerName: 'Settle Dt', width: 110 },
  { field: 'curr_units', headerName: 'Current Units', width: 140, type: 'numericColumn' },
  { field: 'px', headerName: 'Price', width: 110, type: 'numericColumn' },
  { field: 'mv', headerName: 'Market Value', width: 150, type: 'numericColumn' },
  { field: 'bv', headerName: 'Book Value', width: 140, type: 'numericColumn' },
  { field: 'ccy', headerName: 'CCY', width: 80 },
  { field: 'unreal_gl', headerName: 'Unreal G/L', width: 130, type: 'numericColumn' },
  { field: 'byld', headerName: 'Yield', width: 100, type: 'numericColumn' },
  { field: 'dur', headerName: 'Duration', width: 100, type: 'numericColumn' },
  { field: 'issuer', headerName: 'Issuer', width: 200 },
  { field: 'asofdt', headerName: 'As of', width: 110 },
];

const asNumber = (v) => {
  const n = parseFloat(String(v ?? '').replace(/,/g, ''));
  return isNaN(n) ? null : n;
};

/**
 * Values arrive as strings, so formatting and sorting need type-aware handling.
 *
 * Numeric-ness comes from the column's declared `type`, not from a keyword
 * match on its name — `isNumCol` recognises none of mv/bv/px/unreal_gl/byld/dur,
 * which would leave them unformatted and sorted lexicographically ("9" > "10").
 */
function decorateColumns(cols) {
  return cols.map((c) => {
    const numeric = c.type === 'numericColumn' || isNumCol(c.field);
    const dateLike = !numeric && isDateCol(c.field);
    return {
      ...c,
      valueFormatter: (p) => {
        if (numeric) return p.value == null || p.value === '' ? '—' : fmt(asNumber(p.value));
        return formatCell(c.field, p.value);
      },
      comparator: (a, b) => {
        if (numeric) return (asNumber(a) ?? -Infinity) - (asNumber(b) ?? -Infinity);
        if (dateLike) return (toDateObj(a)?.getTime() ?? -1) - (toDateObj(b)?.getTime() ?? -1);
        return String(a || '').localeCompare(String(b || ''));
      },
    };
  });
}

// lot_seq, not acct+id+asofdt: the view is lot-level, so those three collide
// on ~40% of rows (11,016 rows share 6,467 such keys) and AG Grid would
// overwrite or misrender the duplicates.
//
// lot_seq alone is enough because the server numbers it with a row_number()
// carrying no PARTITION BY, which makes it 1..N across the whole response.
// That is the contract this key depends on: partition that row_number and the
// values start repeating, and this has to become a composite key.
const refPositionRowId = (d) => String(d?.lot_seq);

function KPI({ label, value, color }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Card sx={{ width: '100%', p: 1.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>{label}</Typography>
        <Typography sx={{ fontSize: 22, fontWeight: 800, color: color || 'text.primary', lineHeight: 1.1, mt: 0.5 }}>
          {value}
        </Typography>
      </Card>
    </Box>
  );
}

/**
 * Abbreviate a KPI figure.
 *
 * Coerces first: Snowflake serializes DECIMAL columns as strings, and the
 * division branches below survived that by implicit coercion while the final
 * branch called .toFixed on the string and threw — so any KPI under 1,000
 * (easily reached by a narrow filter, or a flat unrealised G/L) crashed the tab.
 */
function fmtCompact(value) {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const a = Math.abs(n);
  if (a >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (a >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toFixed(2);
}

// A chart that failed reads exactly like one with nothing to show unless it
// says so, so the three states are kept apart.
const CHART_LOADING = { loading: true, error: null, data: [] };

/**
 * Whether the server flagged this snapshot as short of a normal row count.
 * Tolerates the flag arriving as a string, which some drivers do with booleans.
 */
const isPartial = (d) => Boolean(d) && (d.is_complete === false || d.is_complete === 'false');

function ChartMessage({ state }) {
  if (state.loading) return <Typography variant="caption" color="text.secondary">Loading…</Typography>;
  if (state.error) {
    return <Typography variant="caption" color="error">Chart unavailable: {state.error}</Typography>;
  }
  return <Typography variant="caption" color="text.secondary">No data.</Typography>;
}

export default function ReferencePositions() {
  const [acct, setAcct] = useState('');
  const [issuer, setIssuer] = useState('');
  const [id, setId] = useState('');
  const [asOf, setAsOf] = useState('');
  const [dates, setDates] = useState([]);
  const [committed, setCommitted] = useState({ acct: '', issuer: '', id: '', asofdt: '' });
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  // The two charts are ancillary rollups. They carry their own loading/error
  // state so one failing rollup degrades its own card instead of taking the
  // positions and KPIs down with it.
  const [issuerChart, setIssuerChart] = useState(CHART_LOADING);
  const [ccyChart, setCcyChart] = useState(CHART_LOADING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // As-of snapshots are independent of the filters, so they load once.
  useEffect(() => {
    let cancelled = false;
    fetchPositionsDates()
      .then((d) => { if (!cancelled) setDates(d || []); })
      .catch(() => { /* the picker is optional; the grid still defaults to latest */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const params = {
      acct: committed.acct || undefined,
      issuer: committed.issuer || undefined,
      id: committed.id || undefined,
      asofdt: committed.asofdt || undefined,
    };
    // The grid and the KPIs describe the same snapshot, so they stand or fall
    // together; a grid without its totals is as misleading as no grid at all.
    Promise.all([fetchPositions(params), fetchPositionsSummary(params)])
      .then(([r, s]) => {
        if (cancelled) return;
        setRows(r || []);
        setSummary(s || null);
        setLoading(false);
      })
      .catch((ex) => {
        if (cancelled) return;
        // Drop the previous datasets too. Leaving them on screen would show
        // rows and KPIs from the old filters while the controls read the new
        // ones — data that contradicts what the user is looking at.
        setRows([]);
        setSummary(null);
        setError(ex.message || String(ex));
        setLoading(false);
      });

    const loadChart = (fetcher, setChart) => {
      setChart(CHART_LOADING);
      fetcher(params)
        .then((d) => { if (!cancelled) setChart({ loading: false, error: null, data: d || [] }); })
        .catch((ex) => {
          if (cancelled) return;
          setChart({ loading: false, error: ex.message || String(ex), data: [] });
        });
    };
    loadChart(fetchPositionsByIssuer, setIssuerChart);
    loadChart(fetchPositionsByCurrency, setCcyChart);
    return () => { cancelled = true; };
  }, [committed]);

  const apply = () => setCommitted({ acct, issuer, id, asofdt: asOf });
  const clear = () => {
    setAcct('');
    setIssuer('');
    setId('');
    setAsOf('');
    setCommitted({ acct: '', issuer: '', id: '', asofdt: '' });
  };

  const columnDefs = useMemo(() => decorateColumns(REF_COLS), []);

  const issuerData = useMemo(
    () => issuerChart.data.filter((r) => r.issuer).map((r) => ({ name: r.issuer, mv: Number(r.total_mv) || 0 })),
    [issuerChart],
  );

  const ccyData = useMemo(
    () => ccyChart.data.filter((r) => r.ccy).map((r) => ({ name: r.ccy, value: Number(r.total_mv) || 0 })),
    [ccyChart],
  );

  // Every query is pinned to one snapshot, so min and max are the same date.
  const asOfLabel = summary?.as_of_max || summary?.as_of_min || '—';
  // A position feed that has quietly stopped refreshing looks identical to a
  // healthy one, so say how old the snapshot is rather than implying it's current.
  const asOfAgeDays = summary?.as_of_age_days;
  // Only meaningful while showing "Latest": age is the signal that the feed has
  // stopped. Deliberately picking a historical snapshot is not a stale feed, and
  // warning about it would contradict the choice the user just made.
  const viewingLatest = !committed.asofdt;
  const stale = viewingLatest && asOfAgeDays != null && asOfAgeDays > STALE_AFTER_DAYS;
  // The grid holding fewer rows than the snapshot counts means the read hit
  // its limit. Say so — otherwise the KPI total and the grid disagree silently.
  const truncated = summary?.total_positions != null && rows.length < summary.total_positions;
  // The server reports whether a snapshot carries its usual row count. A short
  // one is still selectable on purpose, but every total drawn from it is short
  // too, so say that rather than letting the KPIs read as the whole book.
  const shownDate = dates.find((d) => d.asofdt === asOfLabel);
  const partialSnapshot = isPartial(shownDate);

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Snapshot from <strong>clearwater.vpstn_a</strong> (Snowflake) — independent of the upload-based recon flow.
        Figures are scoped to a single as-of date; pick another from the list.
      </Typography>

      <Stack direction="row" spacing={1} mb={2} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField size="small" label="Account" value={acct} onChange={(e) => setAcct(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && apply()} sx={{ width: 170 }} />
        <TextField size="small" label="Issuer" value={issuer} onChange={(e) => setIssuer(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && apply()} sx={{ width: 200 }} />
        <TextField size="small" label="Identifier" value={id} onChange={(e) => setId(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && apply()} sx={{ width: 180 }} />
        <TextField size="small" select label="As of" value={asOf}
          onChange={(e) => setAsOf(e.target.value)} sx={{ width: 200 }}>
          <MenuItem value="">Latest</MenuItem>
          {dates.map((d) => (
            <MenuItem key={d.asofdt} value={d.asofdt}>
              {d.asofdt} ({fmtN(d.n_rows)}){isPartial(d) ? ' — partial load' : ''}
            </MenuItem>
          ))}
        </TextField>
        <Button variant="contained" size="small" onClick={apply} disabled={loading}>Apply</Button>
        {(committed.acct || committed.issuer || committed.id || committed.asofdt) && (
          <Button variant="outlined" size="small" color="error" onClick={clear}>✕ Clear</Button>
        )}
        <Button variant="outlined" size="small" startIcon={<RefreshIcon />}
          onClick={() => setCommitted({ ...committed })} disabled={loading}>
          Refresh
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Failed to load: {error}</Alert>}

      {partialSnapshot && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {asOfLabel} is a partial load — only {fmtN(shownDate.n_rows)} rows, well short of a
          normal snapshot. The totals and charts below cover just those rows.
        </Alert>
      )}

      {stale && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Latest available snapshot is {asOfLabel} — {asOfAgeDays} days old. These are not
          current positions; the upstream feed may have stopped refreshing.
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, mb: 2, width: '100%', flexWrap: 'wrap' }}>
        <KPI label="Total Positions" value={summary ? fmtN(summary.total_positions) : '—'} color="#5b7fff" />
        <KPI label="Distinct Accounts" value={summary ? fmtN(summary.distinct_accounts) : '—'} color="#38bdf8" />
        <KPI label="Distinct Issuers" value={summary ? fmtN(summary.distinct_issuers) : '—'} color="#a78bfa" />
        <KPI label="Total Market Value" value={summary ? fmtCompact(summary.total_market_value) : '—'} color="#34d399" />
        <KPI label="Total Unreal G/L" value={summary ? fmtCompact(summary.total_unreal_gl) : '—'}
          color={summary && summary.total_unreal_gl < 0 ? '#f87171' : '#34d399'} />
        <KPI
          label={
            !viewingLatest ? 'As of (historical)'
              : asOfAgeDays != null ? `As of (${asOfAgeDays}d ago)`
                : 'As of'
          }
          value={asOfLabel}
          color={stale ? '#f87171' : '#fbbf24'}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, width: '100%' }}>
        <Box sx={{ flex: 2, minWidth: 0 }}>
          <Card sx={{ width: '100%', height: 360 }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>Top 10 Issuers by Market Value</Typography>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                {issuerData.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={issuerData} margin={{ left: 10, right: 16, top: 8, bottom: 60 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#33415555" />
                      <XAxis dataKey="name" angle={-30} textAnchor="end" interval={0} height={70}
                        tick={{ fill: '#94a3b8', fontSize: 11 }} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} tickFormatter={(v) => fmtCompact(v)} />
                      <RTooltip formatter={(v) => fmt(v)}
                        contentStyle={{ background: '#1e293b', border: '1px solid #334155' }} />
                      <Bar dataKey="mv" fill="#5b7fff" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <ChartMessage state={issuerChart} />
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Card sx={{ width: '100%', height: 360 }}>
            <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>Market Value by Currency</Typography>
              <Box sx={{ flex: 1, minHeight: 0 }}>
                {ccyData.length ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={ccyData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                        innerRadius="45%" outerRadius="80%" paddingAngle={2} label={(p) => p.name}>
                        {ccyData.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                      </Pie>
                      <RTooltip formatter={(v) => fmt(v)}
                        contentStyle={{ background: '#1e293b', border: '1px solid #334155' }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <ChartMessage state={ccyChart} />
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>

      <Box sx={{ height: 560, width: '100%', position: 'relative' }}>
        {loading && (
          <Box sx={{
            position: 'absolute', inset: 0, zIndex: 5, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            bgcolor: 'rgba(15,23,42,0.45)', borderRadius: 1,
          }}>
            <CircularProgress size={28} />
          </Box>
        )}
        <PamGrid
          rowData={rows}
          columnDefs={columnDefs}
          gridId="derivative-reference-positions"
          getRowId={refPositionRowId}
          defaultColDef={{ sortable: true, resizable: true, filter: true, wrapHeaderText: true, autoHeaderHeight: true }}
          enableShowToolbar={false}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" mt={1} display="block">
        Showing {fmtN(rows.length)}
        {truncated ? ` of ${fmtN(summary.total_positions)}` : ''} rows for {asOfLabel}.
        {truncated
          ? ' The rest were cut off by the server read limit — narrow with a filter to see them.'
          : ' Apply filters to narrow.'}
      </Typography>
    </Box>
  );
}
